import { api } from '@/store/api'
import type { PaginatedResponse } from '@/types/api'
import type { Course, CourseDifficulty } from '@/types/course'
import type { LangFilter } from '@/types/lang'

// GET /courses/ returns a mix of classic + gamified items. The spec shows
// `type: 'gamified' | 'classic'`, but we only model the gamified variant
// strictly. Non-gamified items are filtered out at the page layer.
// TODO: when backend adds `?type=gamified`, drop the client-side filter.
export type CatalogItem = Course | { type: 'classic'; [key: string]: unknown }

export interface CatalogQuery {
  page: number
  limit: number
  search?: string
  categoriesFilter?: number[]
  difficultyFilter?: CourseDifficulty[]
  /** Обязателен: без параметра бэк берёт язык из профиля, а профиль может
   *  ещё не догнать локальный выбор (PATCH /users/me асинхронный). */
  languageFilter: LangFilter
  /** Без значения — «популярные», как было до сортировок. */
  sort?: ExploreSort
}

export type ExploreSort = 'popular' | 'updated' | 'completions'

export interface ExploreQuery {
  page: number
  limit: number
  sort?: ExploreSort
  search?: string
  categoriesFilter?: number[]
  difficultyFilter?: CourseDifficulty[]
  /** Без параметра бэк берёт язык из профиля; страница Explore передаёт его всегда. */
  languageFilter?: LangFilter
}

export const catalogApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCatalog: build.query<PaginatedResponse<CatalogItem>, CatalogQuery>({
      query: (q) => {
        const params = new URLSearchParams()
        params.set('page', String(q.page))
        params.set('limit', String(q.limit))
        params.set('sort', q.sort ?? 'popular')
        params.set('languageFilter', q.languageFilter)
        if (q.search) params.set('searchFilter', q.search)
        q.categoriesFilter?.forEach((id) => params.append('categoriesFilter', String(id)))
        q.difficultyFilter?.forEach((d) => params.append('difficultyFilter', d))
        return `courses/?${params.toString()}`
      },
      providesTags: [{ type: 'Course', id: 'CATALOG' }],
    }),
    // Лента Explore: каталог плюс курсы «по ссылке» с галочкой. Только для авторов
    // (401 без токена, 403 не-автору); параметры те же, что у каталога.
    getExplore: build.query<PaginatedResponse<CatalogItem>, ExploreQuery>({
      query: (q) => {
        const params = new URLSearchParams()
        params.set('page', String(q.page))
        params.set('limit', String(q.limit))
        if (q.sort) params.set('sort', q.sort)
        if (q.languageFilter) params.set('languageFilter', q.languageFilter)
        if (q.search) params.set('searchFilter', q.search)
        q.categoriesFilter?.forEach((id) => params.append('categoriesFilter', String(id)))
        q.difficultyFilter?.forEach((d) => params.append('difficultyFilter', d))
        return `gamified-courses/explore?${params.toString()}`
      },
      providesTags: [{ type: 'Course', id: 'CATALOG' }],
    }),
  }),
})

export const { useGetCatalogQuery, useGetExploreQuery } = catalogApi
